/**
 * Resource Monitor - Continuous capability re-evaluation
 *
 * Periodically checks available CPU, memory, and thermal state.
 * Re-evaluates device capability tier if resources change significantly.
 * Supports Web, Electron iframe, Mobile browsers.
 *
 * Phase 2 Implementation: Adapts to freed/constrained resources
 */

import logger from '../../virtual-background/logger';

import {
    detectThermalThrottling,
    getAvailableMemory,
    getDeviceCapability,
    getEstimatedCpuUsage,
    getQualitySettings
} from './deviceCapabilities';

/**
 * ResourceMonitor - Continuously monitors device resources
 * and adapts virtual background quality settings
 */
export default class ResourceMonitor {
    /**
     * Constructor for ResourceMonitor
     *
     * @param {Object} options - Configuration options
     */
    constructor(options = {}) {
        this._checkInterval = options.checkInterval || 30000; // 30 seconds
        this._memoryChangeThreshold = options.memoryChangeThreshold || 0.2; // 20%
        this._cpuChangeThreshold = options.cpuChangeThreshold || 0.3; // 30%
        this._debounceDelay = options.debounceDelay || 2000; // Wait 2s before changing

        // State tracking
        this._lastAvailableMemory = null;
        this._lastEstimatedCpu = null;
        this._lastDetectedCapability = null;
        this._lastThermalState = null;
        this._isMonitoring = false;
        this._monitorInterval = null;
        this._pendingCapabilityChange = null;
        this._capabilityChangeTimeout = null;

        // Callback for when capability changes
        this._onCapabilityChange = options.onCapabilityChange || (() => {
            // noop
        });

        logger.debug('[ResourceMonitor] Initialized with interval:', this._checkInterval);
    }

    /**
     * Start monitoring resources
     *
     * @returns {void}
     */
    startMonitoring() {
        if (this._isMonitoring) {
            logger.debug('[ResourceMonitor] Already monitoring');

            return;
        }

        this._isMonitoring = true;
        this._lastAvailableMemory = getAvailableMemory();
        this._lastEstimatedCpu = getEstimatedCpuUsage();
        this._lastDetectedCapability = getDeviceCapability();
        this._lastThermalState = detectThermalThrottling(this._lastEstimatedCpu);

        logger.info('[ResourceMonitor] Started monitoring');
        logger.debug('[ResourceMonitor] Initial state:', {
            availableMemory: this._lastAvailableMemory,
            estimatedCpu: this._lastEstimatedCpu,
            capability: this._lastDetectedCapability,
            thermallyThrottled: this._lastThermalState
        });

        // Start periodic checks
        this._monitorInterval = setInterval(
            () => this._checkResources(),
            this._checkInterval
        );

        // Run first check immediately
        this._checkResources();
    }

    /**
     * Stop monitoring resources
     *
     * @returns {void}
     */
    stopMonitoring() {
        if (!this._isMonitoring) {
            return;
        }

        this._isMonitoring = false;
        if (this._monitorInterval) {
            clearInterval(this._monitorInterval);
            this._monitorInterval = null;
        }
        if (this._capabilityChangeTimeout) {
            clearTimeout(this._capabilityChangeTimeout);
            this._capabilityChangeTimeout = null;
        }

        logger.info('[ResourceMonitor] Stopped monitoring');
    }

    /**
     * Check if resources have changed significantly
     *
     * @private
     * @returns {void}
     */
    _checkResources() {
        // Not while the conference is out of sight. The CPU estimate is a busy loop on the main thread, and while
        // the OS throttles a background browser (Windows Energy Saver) that loop both stalls audio playback in the
        // same process and measures the throttling rather than the device - which then downgrades the effect for
        // when the page comes back.
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden'
                && !window.documentPictureInPicture?.window) {
            return;
        }

        try {
            const currentAvailableMemory = getAvailableMemory();
            const currentEstimatedCpu = getEstimatedCpuUsage();
            const currentCapability = getDeviceCapability();
            const currentThermalState = detectThermalThrottling(currentEstimatedCpu);

            const changedReason = [];

            // Check memory change
            if (this._lastAvailableMemory !== null) {
                const memoryChangeRatio = Math.abs(
                    (currentAvailableMemory - this._lastAvailableMemory) / this._lastAvailableMemory
                );

                if (memoryChangeRatio > this._memoryChangeThreshold) {
                    changedReason.push(
                        `memory ${(memoryChangeRatio * 100).toFixed(1)}% `
                        + `(${this._lastAvailableMemory.toFixed(1)}GB → ${currentAvailableMemory.toFixed(1)}GB)`
                    );
                }
            }

            // Check CPU change
            if (this._lastEstimatedCpu !== null) {
                const cpuChangeRatio = Math.abs(
                    (currentEstimatedCpu - this._lastEstimatedCpu) / (this._lastEstimatedCpu || 0.01)
                );

                if (cpuChangeRatio > this._cpuChangeThreshold) {
                    changedReason.push(
                        `CPU ${(cpuChangeRatio * 100).toFixed(1)}% `
                        + `(${(this._lastEstimatedCpu * 100).toFixed(1)}% → ${(currentEstimatedCpu * 100).toFixed(1)}%)`
                    );
                }
            }

            // Check thermal state change
            if (currentThermalState !== this._lastThermalState) {
                changedReason.push(
                    `thermal state: ${this._lastThermalState ? 'throttled' : 'normal'} `
                    + `→ ${currentThermalState ? 'throttled' : 'normal'}`
                );
            }

            // If nothing changed, return early
            if (changedReason.length === 0) {
                logger.debug('[ResourceMonitor] Resources stable', {
                    memory: currentAvailableMemory.toFixed(1),
                    cpu: (currentEstimatedCpu * 100).toFixed(1),
                    capability: currentCapability
                });
                this._lastAvailableMemory = currentAvailableMemory;
                this._lastEstimatedCpu = currentEstimatedCpu;
                this._lastThermalState = currentThermalState;

                return;
            }

            logger.info('[ResourceMonitor] Resources changed:', changedReason.join(', '));

            // Update last known state
            this._lastAvailableMemory = currentAvailableMemory;
            this._lastEstimatedCpu = currentEstimatedCpu;
            this._lastThermalState = currentThermalState;

            // Re-evaluate capability
            this._evaluateCapabilityChange(currentCapability);
        } catch (error) {
            logger.error('[ResourceMonitor] Error checking resources:', error);
        }
    }

    /**
     * Evaluate if capability should change
     *
     * @private
     * @param {string} newCapability - Newly detected capability
     * @returns {void}
     */
    _evaluateCapabilityChange(newCapability) {
        if (newCapability === this._lastDetectedCapability) {
            logger.debug('[ResourceMonitor] Capability unchanged:', newCapability);

            return;
        }

        logger.warn('[ResourceMonitor] Capability change detected:', {
            from: this._lastDetectedCapability,
            to: newCapability
        });

        // Debounce: wait a bit before applying change
        // This prevents rapid oscillation between tiers
        this._pendingCapabilityChange = newCapability;

        if (this._capabilityChangeTimeout) {
            clearTimeout(this._capabilityChangeTimeout);
        }

        this._capabilityChangeTimeout = setTimeout(
            () => this._applyCapabilityChange(newCapability),
            this._debounceDelay
        );

        logger.debug('[ResourceMonitor] Queued capability change, debouncing...');
    }

    /**
     * Apply capability change after debounce
     *
     * @private
     * @param {string} newCapability - Capability to apply
     * @returns {void}
     */
    _applyCapabilityChange(newCapability) {
        // Verify it's still the pending change (might have been cancelled)
        if (newCapability !== this._pendingCapabilityChange) {
            return;
        }

        this._pendingCapabilityChange = null;
        this._capabilityChangeTimeout = null;

        // Get new quality settings
        const qualitySettings = getQualitySettings(newCapability);

        logger.info('[ResourceMonitor] Applying capability change', {
            from: this._lastDetectedCapability,
            to: newCapability,
            newQualitySettings: {
                targetFps: qualitySettings.targetFps || 'N/A',
                renderingScale: qualitySettings.renderingScale,
                useGuidedFilter: qualitySettings.useGuidedFilter,
                guidedFilterRadius: qualitySettings.guidedFilterRadius,
                temporalAlpha: qualitySettings.temporalAlpha
            }
        });

        this._lastDetectedCapability = newCapability;

        // Notify caller to update quality settings
        this._onCapabilityChange({
            newCapability,
            qualitySettings
        });
    }

    /**
     * Cancel pending capability change
     *
     * @returns {void}
     */
    cancelPendingChange() {
        if (this._capabilityChangeTimeout) {
            clearTimeout(this._capabilityChangeTimeout);
            this._capabilityChangeTimeout = null;
        }
        this._pendingCapabilityChange = null;
    }

    /**
     * Get current monitored state
     *
     * @returns {Object} Current state
     */
    getState() {
        return {
            isMonitoring: this._isMonitoring,
            lastAvailableMemory: this._lastAvailableMemory,
            lastEstimatedCpu: this._lastEstimatedCpu,
            lastDetectedCapability: this._lastDetectedCapability,
            lastThermalState: this._lastThermalState,
            hasPendingChange: this._pendingCapabilityChange !== null,
            pendingCapability: this._pendingCapabilityChange
        };
    }

    /**
     * Dispose resources
     *
     * @returns {void}
     */
    dispose() {
        this.stopMonitoring();
    }
}
